// Profile-inferred session defaults for the schedule editor. Header prefs
// (units, currency) are session-only — instead of persisting them we derive
// sensible defaults from the user's profile: their Imperial/Metric preference
// and their country (full names, e.g. "United States", set at signup from IP
// — see LoginState.createProfileIfNotExists).

const EUROZONE = [
  "Austria",
  "Belgium",
  "Croatia",
  "Cyprus",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Portugal",
  "Slovakia",
  "Slovenia",
  "Spain",
];

// Country → code, restricted to the codes CURRENCY_OPTIONS already offers
// (libs/currency.ts CURRENCY_SYMBOLS). Anything unmapped falls back to USD.
const COUNTRY_CURRENCY: Record<string, string> = {
  "united states": "USD",
  "united kingdom": "GBP",
  australia: "AUD",
  canada: "CAD",
  japan: "JPY",
  china: "CNY",
  switzerland: "CHF",
  "hong kong": "HKD",
  singapore: "SGD",
  "new zealand": "NZD",
  sweden: "SEK",
  norway: "NOK",
  denmark: "DKK",
  india: "INR",
  "south korea": "KRW",
  mexico: "MXN",
  brazil: "BRL",
  "south africa": "ZAR",
  "united arab emirates": "AED",
  ...Object.fromEntries(EUROZONE.map((c) => [c.toLowerCase(), "EUR"])),
};

export const currencyForCountry = (country?: string): string =>
  COUNTRY_CURRENCY[(country || "").trim().toLowerCase()] || "USD";

// Project measurementUnit ('Imperial' | 'Metric') → schedule display unit.
export const unitsForMeasurementUnit = (unit?: string): "mm" | "in" =>
  unit === "Imperial" ? "in" : "mm";

// The profile carries the units preference twice (measuring_category is what
// ProfileForm writes; measuringCategory is a legacy duplicate — both are set
// at signup). Values: 'Imperial' | 'Metric'.
export const measurementForProfile = (profile: any): "Imperial" | "Metric" => {
  const category = profile?.measuring_category || profile?.measuringCategory;
  return category === "Imperial" ? "Imperial" : "Metric";
};

export const unitsForProfile = (profile: any): "mm" | "in" =>
  unitsForMeasurementUnit(measurementForProfile(profile));

export const scheduleDefaultsForProfile = (
  profile: any,
): { units: "mm" | "in"; currency: string } => ({
  units: unitsForProfile(profile),
  currency: currencyForCountry(profile?.country),
});

// New-project defaults (#3593): currency from the profile country, units from
// the profile's Imperial/Metric preference — the project doc's enum values.
export const projectDefaultsForProfile = (
  profile: any,
): { currency: string; measurementUnit: "Imperial" | "Metric" } => ({
  currency: currencyForCountry(profile?.country),
  measurementUnit: measurementForProfile(profile),
});
